import { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useNavigate } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Badge from '@/components/common/Badge'
import { TagFilter, TagBadges } from '@/components/common/TagFilter'
import { workflowStore } from '@/stores/WorkflowStore'
import {
  Square3Stack3DIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  PlayIcon,
  PencilIcon,
  TrashIcon,
  FunnelIcon,
  SparklesIcon,
  EyeIcon,
  CubeTransparentIcon,
  ArrowTopRightOnSquareIcon,
  BoltIcon,
  ClockIcon,
} from '@heroicons/react/24/outline'
import { WorkflowStatus, WorkflowTag, ALL_WORKFLOW_TAGS } from '@/types'
import toast from 'react-hot-toast'

const WorkflowsPage = observer(() => {
  const navigate = useNavigate()

  useEffect(() => {
    workflowStore.fetchWorkflows()
    workflowStore.fetchN8NWorkflows()
    workflowStore.fetchN8NBuilders()
  }, [workflowStore.currentPage])

  const getStatusBadgeVariant = (status: WorkflowStatus) => {
    const variants: Record<WorkflowStatus, 'primary' | 'success' | 'warning' | 'gray'> = {
      [WorkflowStatus.DRAFT]: 'gray',
      [WorkflowStatus.ACTIVE]: 'success',
      [WorkflowStatus.ARCHIVED]: 'warning',
    }
    return variants[status]
  }

  const handleDeleteWorkflow = async (id: string, name: string) => {
    if (window.confirm(`Are you sure you want to delete "${name}"?`)) {
      try {
        await workflowStore.deleteWorkflow(id)
        toast.success('Workflow deleted successfully')
      } catch (error) {
        toast.error('Failed to delete workflow')
      }
    }
  }

  const handleCreateWorkflow = () => {
    navigate('/workflows/new')
  }

  const handleExecuteN8NWorkflow = async (workflowId: string, workflowName: string) => {
    try {
      const result = await workflowStore.executeN8NWorkflow(workflowId)
      toast.success(`Workflow "${workflowName}" execution started`)
    } catch (error: any) {
      toast.error(error.message || 'Failed to execute workflow')
    }
  }

  const openN8NEditor = (workflowId: string) => {
    // Open n8n workflow editor in new tab
    const n8nBaseUrl = import.meta.env.VITE_N8N_URL || 'https://n8n.ziro.pub'
    window.open(`${n8nBaseUrl}/workflow/${workflowId}`, '_blank')
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Workflows</h1>
          <p className="text-muted-foreground mt-1">
            Build and manage agent workflows ({workflowStore.totalWorkflows} custom, {workflowStore.n8nWorkflows.length} n8n)
          </p>
        </div>
        <Button
          variant="primary"
          onClick={handleCreateWorkflow}
        >
          <PlusIcon className="w-5 h-5 mr-2" />
          Create Workflow
        </Button>
      </div>

      {/* Ziro Intelligence System Workflow */}
      <Card className="bg-gradient-to-r from-purple-500/15 to-blue-500/15 border-purple-500/20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-purple-500/20 rounded-xl">
              <SparklesIcon className="w-8 h-8 text-purple-600" />
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h3 className="text-lg font-bold text-foreground">
                  Ziro Intelligence Multi-Agent System
                </h3>
                <Badge variant="primary">System Workflow</Badge>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Pre-built intelligent execution workflow with Supervisor, 7 Specialists, AI Review, and Parallel Execution
              </p>
              <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                <span>14 Nodes</span>
                <span>32 Edges</span>
                <span>Conditional Routing</span>
                <span>Quality Assurance</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              onClick={() => navigate('/playground?agent=auto-orchestrator')}
            >
              <EyeIcon className="w-4 h-4 mr-1" />
              View Graph
            </Button>
            <Button
              variant="primary"
              onClick={() => navigate('/playground?agent=auto-orchestrator')}
            >
              <PlayIcon className="w-4 h-4 mr-1" />
              Execute
            </Button>
          </div>
        </div>
      </Card>

      {/* Built-in Workflow Graphs (crews + multi-step agents) */}
      <BuiltinWorkflowsSection navigate={navigate} />

      {/* N8N Workflow Builders Section */}
      {workflowStore.n8nBuilders.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <CubeTransparentIcon className="w-6 h-6 text-orange-500" />
            <h2 className="text-lg font-semibold text-foreground">N8N Workflow Builders</h2>
            <Badge variant="warning">{workflowStore.n8nBuilders.length} Builders</Badge>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {workflowStore.n8nBuilders.map((builder) => (
              <Card key={builder.name} className="bg-gradient-to-r from-orange-500/15 to-amber-500/15 border-orange-500/20 hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-orange-500/20 rounded-lg">
                      <BoltIcon className="w-6 h-6 text-orange-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground">{builder.display_name}</h3>
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{builder.description}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge variant={builder.enabled ? 'success' : 'gray'}>
                          {builder.enabled ? 'Enabled' : 'Disabled'}
                        </Badge>
                        {builder.version && (
                          <span className="text-xs text-muted-foreground">v{builder.version}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => navigate(`/agents/${builder.name}/execute`)}
                    disabled={!builder.enabled}
                  >
                    <SparklesIcon className="w-4 h-4 mr-1" />
                    Build Workflow
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Live N8N Workflows Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Square3Stack3DIcon className="w-6 h-6 text-blue-500" />
            <h2 className="text-lg font-semibold text-foreground">Live N8N Workflows</h2>
            <Badge variant="primary">{workflowStore.n8nWorkflows.length} Workflows</Badge>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => window.open(import.meta.env.VITE_N8N_URL || 'https://n8n.ziro.pub', '_blank')}
          >
            <ArrowTopRightOnSquareIcon className="w-4 h-4 mr-1" />
            Open N8N
          </Button>
        </div>

        {workflowStore.n8nLoading ? (
          <Card className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="text-muted-foreground mt-3">Loading n8n workflows...</p>
          </Card>
        ) : workflowStore.n8nError ? (
          <Card className="bg-red-500/10 border-red-500/20">
            <p className="text-red-600">{workflowStore.n8nError}</p>
            <p className="text-sm text-red-600 mt-1">N8N service may be unavailable. Workflows deployed to n8n will appear here once the service is connected.</p>
          </Card>
        ) : workflowStore.filteredN8NWorkflows.length === 0 ? (
          <Card className="text-center py-8">
            <Square3Stack3DIcon className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">No n8n workflows found</p>
            <p className="text-sm text-muted-foreground mt-1">Use the workflow builders above to create n8n workflows</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {workflowStore.filteredN8NWorkflows.map((workflow) => (
              <Card key={workflow.id} className="hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-foreground truncate">{workflow.name}</h3>
                      <Badge variant={workflow.active ? 'success' : 'gray'}>
                        {workflow.active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <ClockIcon className="w-3.5 h-3.5" />
                      {workflow.updatedAt ? (
                        <span>Updated {formatDistanceToNow(new Date(workflow.updatedAt), { addSuffix: true })}</span>
                      ) : (
                        <span>Recently created</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Tags */}
                {workflow.tags && workflow.tags.length > 0 && (
                  <div className="mb-3">
                    <TagBadges tags={workflow.tags} maxVisible={3} size="sm" />
                  </div>
                )}

                {/* Node count */}
                {workflow.nodes && (
                  <p className="text-sm text-muted-foreground mb-3">
                    {workflow.nodes.length} nodes
                  </p>
                )}

                {/* Actions */}
                <div className="flex items-center gap-2 pt-3 border-t border-border">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => handleExecuteN8NWorkflow(workflow.id, workflow.name)}
                    disabled={!workflow.active}
                  >
                    <PlayIcon className="w-4 h-4 mr-1" />
                    Execute
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => openN8NEditor(workflow.id)}
                  >
                    <ArrowTopRightOnSquareIcon className="w-4 h-4 mr-1" />
                    View in N8N
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="border-t border-border pt-6">
        <h2 className="text-lg font-semibold text-foreground mb-4">Custom Workflows</h2>
      </div>

      {/* Filters and Search */}
      <Card>
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search workflows..."
                  value={workflowStore.searchQuery}
                  onChange={(e) => workflowStore.setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
            <Button
              variant="secondary"
              onClick={() => workflowStore.clearFilters()}
              disabled={!workflowStore.hasActiveFilters}
            >
              <FunnelIcon className="w-5 h-5 mr-2" />
              Clear Filters
            </Button>
          </div>

          <div className="flex items-center gap-4">
            <select
              value={workflowStore.statusFilter}
              onChange={(e) =>
                workflowStore.setStatusFilter(e.target.value as WorkflowStatus | 'all')
              }
              className="px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value={WorkflowStatus.DRAFT}>Draft</option>
              <option value={WorkflowStatus.ACTIVE}>Active</option>
              <option value={WorkflowStatus.ARCHIVED}>Archived</option>
            </select>
          </div>

          {/* Tag Filter */}
          <div>
            <p className="text-sm font-medium text-foreground mb-2">Filter by tags:</p>
            <TagFilter<WorkflowTag>
              tags={ALL_WORKFLOW_TAGS}
              selectedTags={workflowStore.selectedTags}
              onTagToggle={(tag) => workflowStore.toggleTag(tag)}
              onClear={() => workflowStore.clearSelectedTags()}
            />
          </div>
        </div>
      </Card>

      {/* Workflow List */}
      {workflowStore.isLoading ? (
        <Card className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="text-muted-foreground mt-4">Loading workflows...</p>
        </Card>
      ) : workflowStore.filteredWorkflows.length === 0 ? (
        <Card className="text-center py-12">
          <Square3Stack3DIcon className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">
            {workflowStore.hasActiveFilters ? 'No workflows match your filters' : 'No custom workflows yet'}
          </h3>
          <p className="text-muted-foreground mb-4">
            {workflowStore.hasActiveFilters
              ? 'Try adjusting your search or filters'
              : 'Create your first custom workflow to get started'}
          </p>
          {!workflowStore.hasActiveFilters && (
            <Button variant="primary" onClick={handleCreateWorkflow}>
              <PlusIcon className="w-5 h-5 mr-2" />
              Create Workflow
            </Button>
          )}
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {workflowStore.filteredWorkflows.map((workflow) => (
            <Card key={workflow.id} className="hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-foreground">
                      {workflow.name}
                    </h3>
                    <Badge variant={getStatusBadgeVariant(workflow.status)}>
                      {workflow.status}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {workflow.description || 'No description'}
                  </p>
                </div>
              </div>

              <div className="space-y-3 mb-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Nodes:</span>
                  <span className="font-medium text-foreground">
                    {workflow.definition?.nodes?.length || 0}
                  </span>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Connections:</span>
                  <span className="font-medium text-foreground">
                    {workflow.definition?.edges?.length || 0}
                  </span>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Created:</span>
                  <span className="text-foreground">
                    {formatDistanceToNow(new Date(workflow.created_at), { addSuffix: true })}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2 pt-4 border-t border-border">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => navigate(`/workflows/${workflow.id}/execute`)}
                  disabled={workflow.status !== 'active'}
                >
                  <PlayIcon className="w-4 h-4 mr-1" />
                  Execute
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => navigate(`/workflows/${workflow.id}`)}
                >
                  <PencilIcon className="w-4 h-4 mr-1" />
                  Edit
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => handleDeleteWorkflow(workflow.id, workflow.name)}
                >
                  <TrashIcon className="w-4 h-4 mr-1" />
                  Delete
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {workflowStore.totalWorkflows > workflowStore.pageSize && (
        <Card>
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Showing {(workflowStore.currentPage - 1) * workflowStore.pageSize + 1} to{' '}
              {Math.min(
                workflowStore.currentPage * workflowStore.pageSize,
                workflowStore.totalWorkflows
              )}{' '}
              of {workflowStore.totalWorkflows} workflows
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => workflowStore.setPage(workflowStore.currentPage - 1)}
                disabled={workflowStore.currentPage === 1}
              >
                Previous
              </Button>
              <span className="text-sm text-foreground">
                Page {workflowStore.currentPage} of{' '}
                {Math.ceil(workflowStore.totalWorkflows / workflowStore.pageSize)}
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => workflowStore.setPage(workflowStore.currentPage + 1)}
                disabled={
                  workflowStore.currentPage >=
                  Math.ceil(workflowStore.totalWorkflows / workflowStore.pageSize)
                }
              >
                Next
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Error Display */}
      {workflowStore.error && (
        <Card className="bg-red-500/10 border-red-500/20">
          <div className="flex items-center justify-between">
            <p className="text-red-600">{workflowStore.error}</p>
            <Button variant="ghost" size="sm" onClick={() => workflowStore.clearError()}>
              Dismiss
            </Button>
          </div>
        </Card>
      )}
    </div>
  )
})

// ── Built-in Workflows Section ──
// Shows all registered workflow graphs from the unified backend endpoint
interface BuiltinWorkflow {
  id: string
  name: string
  description: string
  category: string
  node_count: number
  edge_count: number
}

function BuiltinWorkflowsSection({ navigate }: { navigate: ReturnType<typeof useNavigate> }) {
  const [workflows, setWorkflows] = useState<BuiltinWorkflow[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchWorkflows = async () => {
      try {
        const { apiClient } = await import('@/services/api')
        const resp = await (apiClient as any).client.get('/api/v1/workflows/graphs/summary')
        setWorkflows(resp.data?.workflows || [])
      } catch (err) {
        console.error('Failed to fetch built-in workflows:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchWorkflows()
  }, [])

  if (loading) {
    return (
      <Card className="p-4">
        <p className="text-sm text-muted-foreground">Loading built-in workflows...</p>
      </Card>
    )
  }

  if (workflows.length === 0) {
    return null
  }

  // Group by category
  const byCategory: Record<string, BuiltinWorkflow[]> = {}
  workflows.forEach((wf) => {
    const cat = wf.category || 'Uncategorized'
    if (!byCategory[cat]) byCategory[cat] = []
    byCategory[cat].push(wf)
  })

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <SparklesIcon className="w-6 h-6 text-emerald-500" />
        <h2 className="text-lg font-semibold text-foreground">Built-in Workflows</h2>
        <Badge variant="gray">{workflows.length}</Badge>
      </div>

      {Object.entries(byCategory).sort().map(([category, wfs]) => (
        <div key={category}>
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
            {category}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {wfs.map((wf) => (
              <Card key={wf.id} className="p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-foreground truncate">{wf.name}</h3>
                    <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
                      {wf.description}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-[10px] text-muted-foreground mt-2">
                  <span>{wf.node_count} nodes</span>
                  <span>{wf.edge_count} edges</span>
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <button
                    onClick={() => navigate(`/playground?agent=${encodeURIComponent(wf.id)}`)}
                    className="flex-1 px-3 py-1.5 text-xs font-medium rounded bg-muted text-foreground hover:bg-border transition-colors"
                  >
                    View Graph
                  </button>
                  <button
                    onClick={() => navigate(`/architect?load=builtin:${wf.id}`)}
                    className="flex-1 px-3 py-1.5 text-xs font-medium rounded bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                  >
                    Edit
                  </button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

export default WorkflowsPage
